import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, ClipboardList, CheckCircle2, ArrowRight, Search, PackagePlus } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/apiClient";
import { useCart } from "../context/CartContext";
import { fmtPrice } from "../data/site";

const businessTypes = ["Grocery Store", "Gas Station", "Restaurant", "Supermarket", "Convenience Store", "Wholesaler", "Office / Business", "Online Seller", "Other"];
const empty = {
    customer_name: "", business_name: "", business_type: "", phone: "", email: "",
    delivery_address: "", city: "", state: "", zip_code: "", preferred_date: "", notes: "", manual_request: "", request_account: false,
};

const STEPS = [
    "Browse products",
    "Add items and quantities",
    "Submit request — our team will confirm pricing and delivery",
];

export default function OrderRequest() {
    const { items, updateCases, updateNote, removeItem, clear } = useCart();
    const [form, setForm] = useState(empty);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const estTotal = items.reduce((s, i) => s + (i.wholesale_price_per_case || 0) * i.cases, 0);

    const submit = async (e) => {
        e.preventDefault();
        if (items.length === 0 && !form.manual_request.trim()) {
            toast.error("Your order request is empty. Add products from the catalog or describe what you need.");
            return;
        }
        if (!form.customer_name || !form.business_name || !form.phone || !form.email) {
            toast.error("Please fill in your name, business, phone and email.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/orders", {
                ...form,
                items: items.map((i) => ({
                    product_id: i.product_id, name: i.name, brand: i.brand, unit_size: i.unit_size,
                    units_per_case: i.units_per_case, cases: i.cases, note: i.note || "",
                    wholesale_price_per_case: i.wholesale_price_per_case,
                })),
            });
            clear();
            setDone(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error("Something went wrong. Please try again or contact us directly.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="order-success">
                <div className="max-w-lg text-center">
                    <CheckCircle2 size={64} className="text-primary mx-auto mb-6" />
                    <h1 className="font-heading font-black text-4xl tracking-tight mb-4">Thank you</h1>
                    <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                        Your order request has been received. Our team will contact you shortly to confirm availability, pricing, and delivery.
                    </p>
                    <div className="flex gap-4 justify-center">
                        <Link to="/catalog" className="bg-primary text-primary-foreground px-7 py-3.5 font-semibold hover:bg-secondary hover:text-secondary-foreground transition-colors">Continue Browsing</Link>
                        <Link to="/" className="border border-border px-7 py-3.5 font-semibold hover:border-primary transition-colors">Home</Link>
                    </div>
                </div>
            </div>
        );
    }

    const inputCls = "w-full border border-border bg-transparent px-4 py-3 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary";

    return (
        <div data-testid="order-page">
            <div className="bg-primary text-primary-foreground">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
                    <p className="text-sm uppercase tracking-[0.2em] text-secondary mb-3">No Payment Required</p>
                    <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Place Wholesale Order Request</h1>
                    <p className="text-primary-foreground/75 mt-3 max-w-2xl">No online payment required. Add products to your request and our team will confirm pricing, availability, and delivery.</p>
                    <div className="grid sm:grid-cols-3 gap-px bg-primary-foreground/10 border border-primary-foreground/10 mt-8">
                        {STEPS.map((s, i) => (
                            <div key={i} className="bg-primary p-5 flex items-start gap-3">
                                <span className="w-7 h-7 bg-secondary text-secondary-foreground flex items-center justify-center font-heading font-black text-sm shrink-0">{i + 1}</span>
                                <span className="text-sm text-primary-foreground/85">{s}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-[1fr_400px] gap-10 items-start">
                {/* Form */}
                <form onSubmit={submit} className="order-2 lg:order-1" data-testid="order-form">
                    <h2 className="font-heading font-bold text-2xl mb-6">Your Business Details</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div><label className="text-xs uppercase tracking-wider text-muted-foreground">Customer Name *</label><input className={inputCls} data-testid="order-customer-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} /></div>
                        <div><label className="text-xs uppercase tracking-wider text-muted-foreground">Business Name *</label><input className={inputCls} data-testid="order-business-name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} /></div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground">Business Type</label>
                            <select className={inputCls} data-testid="order-business-type" value={form.business_type} onChange={(e) => set("business_type", e.target.value)}>
                                <option value="">Select type</option>
                                {businessTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div><label className="text-xs uppercase tracking-wider text-muted-foreground">Phone *</label><input className={inputCls} data-testid="order-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
                        <div><label className="text-xs uppercase tracking-wider text-muted-foreground">Email *</label><input type="email" className={inputCls} data-testid="order-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
                        <div><label className="text-xs uppercase tracking-wider text-muted-foreground">Preferred Delivery Date</label><input type="date" className={inputCls} data-testid="order-date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} /></div>
                        <div className="sm:col-span-2"><label className="text-xs uppercase tracking-wider text-muted-foreground">Delivery Address</label><input className={inputCls} data-testid="order-address" value={form.delivery_address} onChange={(e) => set("delivery_address", e.target.value)} /></div>
                        <div><label className="text-xs uppercase tracking-wider text-muted-foreground">City</label><input className={inputCls} data-testid="order-city" value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><label className="text-xs uppercase tracking-wider text-muted-foreground">State</label><input className={inputCls} data-testid="order-state" value={form.state} onChange={(e) => set("state", e.target.value)} /></div>
                            <div><label className="text-xs uppercase tracking-wider text-muted-foreground">ZIP</label><input className={inputCls} data-testid="order-zip" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} /></div>
                        </div>
                        <div className="sm:col-span-2"><label className="text-xs uppercase tracking-wider text-muted-foreground">Additional Notes</label><textarea rows={3} className={inputCls} data-testid="order-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
                    </div>

                    <div className="mt-5 border border-dashed border-border p-4 bg-muted/40">
                        <label className="flex items-center gap-2 text-sm font-semibold mb-2"><PackagePlus size={16} className="text-primary" /> Looking for a product not listed?</label>
                        <input className={inputCls} data-testid="order-manual-request" placeholder="Enter product name, brand, or case quantity needed." value={form.manual_request} onChange={(e) => set("manual_request", e.target.value)} />
                    </div>

                    <label className="flex items-center gap-3 mt-5 cursor-pointer" data-testid="order-account-request">
                        <input type="checkbox" className="w-4 h-4 accent-[hsl(158,54%,18%)]" checked={form.request_account} onChange={(e) => set("request_account", e.target.checked)} />
                        <span className="text-sm">Request wholesale account setup</span>
                    </label>

                    <p className="text-xs text-muted-foreground leading-relaxed mt-6 border-t border-border pt-5" data-testid="order-disclaimer">
                        By submitting this form, you are sending an order request only. Final pricing, stock availability, delivery schedule, and payment method will be confirmed by YATTO LLC.
                    </p>

                    <button type="submit" disabled={submitting} data-testid="submit-order-request" className="mt-5 w-full sm:w-auto flex items-center justify-center gap-2 bg-primary text-primary-foreground px-10 py-4 font-semibold hover:bg-secondary hover:text-secondary-foreground transition-colors disabled:opacity-60">
                        {submitting ? "Submitting..." : <>Submit Order Request <ArrowRight size={18} /></>}
                    </button>
                </form>

                {/* Cart summary */}
                <div className="order-1 lg:order-2 lg:sticky lg:top-28 border border-border bg-card" data-testid="order-cart">
                    <div className="bg-foreground text-background p-5 flex items-center justify-between">
                        <h2 className="font-heading font-bold text-lg flex items-center gap-2"><ClipboardList size={18} /> Your Request</h2>
                        <span className="text-sm text-background/70">{items.length} item{items.length !== 1 ? "s" : ""}</span>
                    </div>
                    {items.length === 0 ? (
                        <div className="p-8 text-center">
                            <p className="text-muted-foreground mb-5">No products selected yet. Please browse the catalog and add products to your order request.</p>
                            <Link to="/catalog" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 font-semibold text-sm hover:bg-secondary hover:text-secondary-foreground transition-colors"><Search size={15} /> Browse Products</Link>
                        </div>
                    ) : (
                        <>
                            <div className="max-h-[480px] overflow-y-auto divide-y divide-border">
                                {items.map((i) => (
                                    <div key={i.product_id} className="p-4" data-testid={`cart-item-${i.product_id}`}>
                                        <div className="flex gap-3">
                                            {i.image && <img src={i.image} alt={i.name} className="w-14 h-14 object-cover shrink-0 border border-border" />}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold leading-tight">{i.name}</p>
                                                <p className="text-xs text-muted-foreground">{i.brand} · {i.unit_size}{i.units_per_case ? ` · ${i.units_per_case}/case` : ""}</p>
                                                <div className="flex items-center justify-between mt-2">
                                                    <div className="flex items-center border border-border">
                                                        <button type="button" onClick={() => updateCases(i.product_id, i.cases - 1)} className="px-2 py-0.5 hover:bg-muted" data-testid={`cart-minus-${i.product_id}`}><Minus size={12} /></button>
                                                        <span className="px-2 text-sm font-semibold" data-testid={`cart-qty-${i.product_id}`}>{i.cases}</span>
                                                        <button type="button" onClick={() => updateCases(i.product_id, i.cases + 1)} className="px-2 py-0.5 hover:bg-muted" data-testid={`cart-plus-${i.product_id}`}><Plus size={12} /></button>
                                                        <span className="px-2 text-[10px] uppercase text-muted-foreground">cases</span>
                                                    </div>
                                                    <button type="button" onClick={() => removeItem(i.product_id)} className="text-muted-foreground hover:text-destructive" data-testid={`cart-remove-${i.product_id}`}><Trash2 size={15} /></button>
                                                </div>
                                            </div>
                                            {i.wholesale_price_per_case != null && (
                                                <span className="text-sm font-heading font-bold text-primary shrink-0">{fmtPrice(i.wholesale_price_per_case * i.cases)}</span>
                                            )}
                                        </div>
                                        <input
                                            className="w-full mt-2 border border-border bg-transparent px-3 py-1.5 text-xs focus:outline-none focus:border-primary"
                                            placeholder="Add a note for this item (optional)"
                                            value={i.note || ""}
                                            onChange={(e) => updateNote(i.product_id, e.target.value)}
                                            data-testid={`cart-note-${i.product_id}`}
                                        />
                                    </div>
                                ))}
                            </div>
                            <div className="p-5 border-t border-border">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm text-muted-foreground">Estimated total (cases)</span>
                                    <span className="font-heading font-black text-xl text-primary" data-testid="cart-total">{fmtPrice(estTotal)}</span>
                                </div>
                                <p className="text-[11px] text-muted-foreground">Final pricing confirmed by our team. No online payment.</p>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
